import type { BaseElement } from 'slate';

import type { UUID } from '../../../types/type-helpers';
import type { AlineText, SuggestedDrawingEdit } from '../../internal';

export const drawingType = 'drawing' as const;
export type DrawingType = typeof drawingType;

/**
 * OpenXML Drawing uses EMUs (English Metric Units) for measurements.
 * Where 1 pixel = ~9525 EMUs. Credit: https://stackoverflow.com/a/20196486.
 * Most of the measurements below are in EMUs.
 */
export type EMU = string;

export interface cNvPr {
  descr?: string;
  id?: string;
  name?: string;
  title?: string;
}

export interface spPr {
  xfrm?: {
    /* Extents. Width and height in EMUs. */
    ext?: {
      cx: EMU;
      cy: EMU;
    };
    flipH?: string;
    flipV?: string;
    /* Offsets. */
    off?: {
      x: EMU;
      y: EMU;
    };
    /* In 60,000ths of a degree. e.g., 5400000 is 90 degrees. */
    rot?: string;
  };
}

export type DrawingEmbed = { embed: string };
export type DrawingLink = { link: string };
export type DrawingBlip = DrawingEmbed | DrawingLink;
export type DrawingBlipFill = {
  blip?: DrawingBlip;
  srcRect?: {
    b: EMU;
    l: EMU;
    r: EMU;
    t: EMU;
  };
};

export interface DrawingPic {
  blipFill?: DrawingBlipFill;
  nvPicPr?: {
    cNvPicPr?: {
      preferRelativeResize?: string;
    };
    cNvPr?: cNvPr;
  };
  /* Shape properties. */
  spPr?: spPr;
}
export interface DrawingGraphicData {
  pic?: DrawingPic;
}

export interface DrawingGraphic {
  graphicData: DrawingGraphicData;
}

export interface InlineDrawing {
  graphic: DrawingGraphic;
  docPr?: {
    id: string;
    descr?: string;
    name?: string;
    title?: string;
  };
  /* Width and height of the drawing frame. */
  extent?: {
    cx: EMU;
    cy: EMU;
  };
}

export interface DrawingElement extends BaseElement {
  children: AlineText[];
  drawingImgId: UUID;
  type: DrawingType;
  inline?: InlineDrawing;
  suggestedEdits?: SuggestedDrawingEdit[] | null;
}

export type DrawingProps = Omit<DrawingElement, 'children'>;
